import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ViewListIcon from '@mui/icons-material/ViewList';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { DragEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoHint from './InfoHint';
import useRequest from '../../hooks/useRequest';
import type {
    SalaryEmployee,
    SalaryHoliday,
    SalaryScheduleEntry,
    SalaryShift,
} from '../../domain/salary/types';

interface Props {
    employees: SalaryEmployee[];
    shifts: SalaryShift[];
    holidays: SalaryHoliday[];
}

interface ScheduleResponse {
    success: boolean;
    data: SalaryScheduleEntry[];
}

interface AutoGenerateResponse {
    success: boolean;
    data: { inserted: number; proposed?: ProposedEntry[] };
}

interface ProposedEntry {
    employeeId: number;
    date: string;
    shiftId: number;
    isHoliday: boolean;
    isWeekend: boolean;
}

interface DragPayload {
    kind: 'employee' | 'entry';
    employeeId: number;
    entryId?: number;
}

const DRAG_MIME = 'application/x-salary-drag';

const today = new Date();

const isWeekendIso = (iso: string): boolean => {
    const [y, m, d] = iso.split('-').map(Number);
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    return dow === 0 || dow === 6;
};

const pad2 = (value: number): string => String(value).padStart(2, '0');

const buildMonthGrid = (year: number, month: number): (string | null)[][] => {
    const firstDay = new Date(Date.UTC(year, month - 1, 1));
    const lastDay = new Date(Date.UTC(year, month, 0));
    // Week starts Monday.
    const startOffset = (firstDay.getUTCDay() + 6) % 7;
    const weeks: (string | null)[][] = [];
    let currentWeek: (string | null)[] = Array<string | null>(startOffset).fill(null);
    for (let day = 1; day <= lastDay.getUTCDate(); day += 1) {
        currentWeek.push(`${year}-${pad2(month)}-${pad2(day)}`);
        if (currentWeek.length === 7) {
            weeks.push(currentWeek);
            currentWeek = [];
        }
    }
    if (currentWeek.length > 0) {
        while (currentWeek.length < 7) currentWeek.push(null);
        weeks.push(currentWeek);
    }
    return weeks;
};

const computeMondayConflicts = (entries: SalaryScheduleEntry[]): Set<string> => {
    // Key = `${employeeId}:${iso}` for Mondays that follow a worked weekend.
    const weekendDays = new Map<number, string[]>();
    for (const entry of entries) {
        if (!isWeekendIso(entry.date)) continue;
        const list = weekendDays.get(entry.employeeId) ?? [];
        list.push(entry.date);
        weekendDays.set(entry.employeeId, list);
    }
    const conflicts = new Set<string>();
    for (const entry of entries) {
        const [y, m, d] = entry.date.split('-').map(Number);
        const date = new Date(Date.UTC(y, m - 1, d));
        if (date.getUTCDay() !== 1) continue;
        const list = weekendDays.get(entry.employeeId) ?? [];
        for (const w of list) {
            const [wy, wm, wd] = w.split('-').map(Number);
            const wdate = new Date(Date.UTC(wy, wm - 1, wd));
            const diff = (date.getTime() - wdate.getTime()) / 86_400_000;
            if (diff === 1 || diff === 2) {
                conflicts.add(`${entry.employeeId}:${entry.date}`);
                break;
            }
        }
    }
    return conflicts;
};

const ScheduleTab = ({ employees, shifts, holidays }: Props) => {
    const { t } = useTranslation();
    const request = useRequest();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [entries, setEntries] = useState<SalaryScheduleEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [deletingMonth, setDeletingMonth] = useState(false);
    const [proposal, setProposal] = useState<ProposedEntry[] | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<'delete' | 'regenerate' | null>(null);
    const [view, setView] = useState<'calendar' | 'list'>('calendar');
    const [form, setForm] = useState({ employeeId: 0, shiftId: 0, date: '' });
    const [snack, setSnack] = useState<{
        open: boolean;
        severity: 'success' | 'error' | 'info' | 'warning';
        message: string;
    }>({ open: false, severity: 'success', message: '' });

    const showSnack = (severity: 'success' | 'error' | 'info' | 'warning', message: string) =>
        setSnack({ open: true, severity, message });

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const response = await request<ScheduleResponse>({
                path: '/salary/schedule',
                params: { year, month },
            });
            setEntries(response.data ?? []);
        } catch (error) {
            showSnack('error', (error as Error).message);
        } finally {
            setLoading(false);
        }
    }, [request, year, month]);

    useEffect(() => {
        reload();
    }, [reload]);

    const holidaySet = useMemo(() => new Set(holidays.map(h => h.date)), [holidays]);
    const mondayConflicts = useMemo(() => computeMondayConflicts(entries), [entries]);
    const employeeById = useMemo(() => new Map(employees.map(e => [e.id, e])), [employees]);
    const shiftById = useMemo(() => new Map(shifts.map(s => [s.id, s])), [shifts]);

    const weeks = useMemo(() => buildMonthGrid(year, month), [year, month]);

    const entriesByDate = useMemo(() => {
        const map = new Map<string, SalaryScheduleEntry[]>();
        for (const entry of entries) {
            const list = map.get(entry.date) ?? [];
            list.push(entry);
            map.set(entry.date, list);
        }
        return map;
    }, [entries]);

    const applicableShifts = useCallback(
        (iso: string): SalaryShift[] => {
            const weekend = isWeekendIso(iso);
            const weekendShifts = shifts.filter(s => s.isWeekendVariant);
            const weekdayShifts = shifts.filter(s => !s.isWeekendVariant);
            if (weekend && weekendShifts.length > 0) return weekendShifts;
            return weekendShifts.length > 0 && weekdayShifts.length === 0
                ? weekendShifts
                : weekdayShifts;
        },
        [shifts],
    );

    const handleAutoGenerate = async () => {
        setGenerating(true);
        try {
            const response = await request<AutoGenerateResponse>({
                path: '/salary/schedule/auto-generate',
                method: 'POST',
                params: { year, month, dryRun: true },
            });
            const proposed = response.data?.proposed ?? [];
            if (proposed.length === 0) {
                showSnack('warning', t('salary.schedule.autoGenerateEmpty'));
                return;
            }
            setProposal(proposed);
        } catch (error) {
            showSnack('error', (error as Error).message);
        } finally {
            setGenerating(false);
        }
    };

    const handleAcceptProposal = async () => {
        if (!proposal) return;
        setCommitting(true);
        try {
            const response = await request<AutoGenerateResponse>({
                path: '/salary/schedule/auto-generate/commit',
                method: 'POST',
                params: { entries: proposal },
            });
            await reload();
            const inserted = response.data?.inserted ?? 0;
            showSnack('success', t('salary.schedule.autoGenerateResult', { count: inserted }));
            setProposal(null);
        } catch (error) {
            showSnack('error', (error as Error).message);
        } finally {
            setCommitting(false);
        }
    };

    const handleRejectProposal = () => setProposal(null);

    const deleteMonth = useCallback(async (): Promise<boolean> => {
        setDeletingMonth(true);
        try {
            const response = await request<{ success: boolean; data: { deleted: number } }>({
                path: `/salary/schedule/month/${year}/${month}`,
                method: 'DELETE',
            });
            await reload();
            showSnack(
                'success',
                t('salary.schedule.deleteMonthResult', {
                    count: response.data?.deleted ?? 0,
                }),
            );
            return true;
        } catch (error) {
            showSnack('error', (error as Error).message);
            return false;
        } finally {
            setDeletingMonth(false);
        }
    }, [request, reload, year, month, t]);

    const handleDeleteMonthConfirmed = async () => {
        const action = confirmDelete;
        setConfirmDelete(null);
        const ok = await deleteMonth();
        if (ok && action === 'regenerate') {
            await handleAutoGenerate();
        }
    };

    const createEntry = useCallback(
        async (employeeId: number, shiftId: number, date: string) => {
            // Allow multiple shifts per day per employee, but prevent the
            // exact same (employee, shift, date) combination from being
            // created twice.
            const dayEntries = entriesByDate.get(date) ?? [];
            if (dayEntries.some(e => e.employeeId === employeeId && e.shiftId === shiftId)) {
                showSnack('warning', t('salary.schedule.duplicateAssignment'));
                return;
            }
            try {
                await request({
                    path: '/salary/schedule',
                    method: 'POST',
                    params: {
                        employeeId,
                        shiftId,
                        date,
                        isWeekend: isWeekendIso(date),
                        isHoliday: holidaySet.has(date),
                    },
                });
                await reload();
            } catch (error) {
                showSnack('error', (error as Error).message);
            }
        },
        [entriesByDate, holidaySet, reload, request, t],
    );

    const handleAddManual = async () => {
        if (!form.employeeId || !form.shiftId || !form.date) return;
        await createEntry(form.employeeId, form.shiftId, form.date);
        setForm({ ...form, date: '' });
    };

    const handleDeleteEntry = async (id: number) => {
        try {
            await request({ path: `/salary/schedule/${id}`, method: 'DELETE' });
            await reload();
        } catch (error) {
            showSnack('error', (error as Error).message);
        }
    };

    const handleDragStart = (event: DragEvent<HTMLDivElement>, payload: DragPayload) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
        // Fallback for browsers that require text/plain.
        event.dataTransfer.setData('text/plain', String(payload.employeeId));
    };

    const readPayload = (event: DragEvent<HTMLDivElement>): DragPayload | null => {
        const raw = event.dataTransfer.getData(DRAG_MIME);
        if (!raw) return null;
        try {
            return JSON.parse(raw) as DragPayload;
        } catch {
            return null;
        }
    };

    const handleDropOnSlot = async (
        event: DragEvent<HTMLDivElement>,
        date: string,
        shiftId: number,
    ) => {
        event.preventDefault();
        const payload = readPayload(event);
        if (!payload) return;
        // If dragging an existing entry, delete it first (move semantics).
        if (payload.kind === 'entry' && payload.entryId !== undefined) {
            // If dropped on the same slot/date, do nothing.
            const existing = entries.find(e => e.id === payload.entryId);
            if (existing?.date === date && existing.shiftId === shiftId) return;
            try {
                await request({
                    path: `/salary/schedule/${payload.entryId}`,
                    method: 'DELETE',
                });
            } catch (error) {
                showSnack('error', (error as Error).message);
                return;
            }
        }
        await createEntry(payload.employeeId, shiftId, date);
    };

    const allowDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    const employeeLabel = (id: number) => employeeById.get(id)?.name ?? `#${id}`;
    const shiftLabel = (id: number) => {
        const shift = shiftById.get(id);
        if (!shift) return '-';
        return `${shift.name} (${shift.startTime}-${shift.endTime})`;
    };

    const weekdayHeaders = [0, 1, 2, 3, 4, 5, 6].map(index =>
        t(`salary.schedule.weekdays.${index}`),
    );

    return (
        <Stack spacing={2}>
            <Paper sx={{ p: 2 }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                >
                    <TextField
                        size="small"
                        type="number"
                        label={t('salary.common.year')}
                        value={year}
                        onChange={e => setYear(Number(e.target.value))}
                    />
                    <TextField
                        size="small"
                        type="number"
                        label={t('salary.common.month')}
                        value={month}
                        onChange={e => setMonth(Number(e.target.value))}
                        slotProps={{ htmlInput: { min: 1, max: 12 } }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<AutoFixHighIcon />}
                        onClick={handleAutoGenerate}
                        disabled={generating}
                    >
                        {generating ? (
                            <CircularProgress size={20} />
                        ) : (
                            t('salary.schedule.autoGenerate')
                        )}
                    </Button>
                    <InfoHint title={t('salary.info.schedule.autoGenerate')} />
                    <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<RestartAltIcon />}
                        onClick={() => setConfirmDelete('regenerate')}
                        disabled={generating || deletingMonth || entries.length === 0}
                    >
                        {t('salary.schedule.regenerateMonth')}
                    </Button>
                    <InfoHint title={t('salary.info.schedule.regenerate')} />
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteSweepIcon />}
                        onClick={() => setConfirmDelete('delete')}
                        disabled={deletingMonth || entries.length === 0}
                    >
                        {deletingMonth ? (
                            <CircularProgress size={20} />
                        ) : (
                            t('salary.schedule.deleteMonth')
                        )}
                    </Button>
                    <InfoHint title={t('salary.info.schedule.deleteMonth')} />
                    <Box flex={1} />
                    <ToggleButtonGroup
                        size="small"
                        value={view}
                        exclusive
                        onChange={(_event, value) => value && setView(value)}
                    >
                        <ToggleButton value="calendar">
                            <CalendarMonthIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="list">
                            <ViewListIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <InfoHint title={t('salary.info.schedule.viewToggle')} />
                </Stack>
            </Paper>

            {view === 'calendar' && (
                <Paper sx={{ p: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Box sx={{ width: { xs: '100%', md: 220 } }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('salary.schedule.dragEmployeesHint')}
                                <InfoHint title={t('salary.info.schedule.dragEmployees')} />
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {employees
                                    .filter(e => e.isActive)
                                    .map(employee => (
                                        <Chip
                                            key={employee.id}
                                            label={employee.name}
                                            color="primary"
                                            variant="outlined"
                                            size="small"
                                            draggable
                                            onDragStart={event =>
                                                handleDragStart(event, {
                                                    kind: 'employee',
                                                    employeeId: employee.id,
                                                })
                                            }
                                            sx={{ cursor: 'grab', mb: 0.5 }}
                                        />
                                    ))}
                                {employees.filter(e => e.isActive).length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        {t('salary.employees.noData')}
                                    </Typography>
                                )}
                            </Stack>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            {loading ? (
                                <Stack direction="row" justifyContent="center" p={3}>
                                    <CircularProgress />
                                </Stack>
                            ) : (
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(7, 1fr)',
                                            gap: 0.5,
                                            mb: 0.5,
                                        }}
                                    >
                                        {weekdayHeaders.map(label => (
                                            <Typography
                                                key={label}
                                                variant="caption"
                                                align="center"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {label}
                                            </Typography>
                                        ))}
                                    </Box>
                                    {weeks.map((week, weekIndex) => (
                                        <Box
                                            key={`week-${weekIndex.toString()}`}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(7, 1fr)',
                                                gap: 0.5,
                                                mb: 0.5,
                                            }}
                                        >
                                            {week.map((iso, dayIndex) => {
                                                if (!iso) {
                                                    return (
                                                        <Box
                                                            key={`empty-${weekIndex.toString()}-${dayIndex.toString()}`}
                                                            sx={{ minHeight: 120 }}
                                                        />
                                                    );
                                                }
                                                return (
                                                    <DayCell
                                                        key={iso}
                                                        iso={iso}
                                                        isHoliday={holidaySet.has(iso)}
                                                        isWeekend={isWeekendIso(iso)}
                                                        shifts={applicableShifts(iso)}
                                                        dayEntries={entriesByDate.get(iso) ?? []}
                                                        employeeLabel={employeeLabel}
                                                        mondayConflicts={mondayConflicts}
                                                        onDrop={handleDropOnSlot}
                                                        onDragStart={handleDragStart}
                                                        onDragOver={allowDrop}
                                                        onDeleteEntry={handleDeleteEntry}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Stack>
                </Paper>
            )}

            {view === 'list' && (
                <>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('salary.schedule.addEntry')}
                        </Typography>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                        >
                            <TextField
                                size="small"
                                select
                                label={t('salary.schedule.employee')}
                                value={form.employeeId || ''}
                                onChange={e =>
                                    setForm({ ...form, employeeId: Number(e.target.value) })
                                }
                                sx={{ minWidth: 200 }}
                            >
                                {employees.map(employee => (
                                    <MenuItem key={employee.id} value={employee.id}>
                                        {employee.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                size="small"
                                select
                                label={t('salary.schedule.shift')}
                                value={form.shiftId || ''}
                                onChange={e =>
                                    setForm({ ...form, shiftId: Number(e.target.value) })
                                }
                                sx={{ minWidth: 200 }}
                            >
                                {shifts.map(shift => (
                                    <MenuItem key={shift.id} value={shift.id}>
                                        {shiftLabel(shift.id)}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                size="small"
                                type="date"
                                label={t('salary.schedule.date')}
                                value={form.date}
                                onChange={e => setForm({ ...form, date: e.target.value })}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                            <Button variant="contained" onClick={handleAddManual}>
                                {t('common.add')}
                            </Button>
                        </Stack>
                    </Paper>

                    <Paper>
                        {loading ? (
                            <Stack direction="row" justifyContent="center" p={3}>
                                <CircularProgress />
                            </Stack>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Box
                                    component="table"
                                    sx={{ width: '100%', borderCollapse: 'collapse' }}
                                >
                                    <Box component="thead">
                                        <Box component="tr">
                                            <HeaderCell label={t('salary.schedule.date')} />
                                            <HeaderCell label={t('salary.schedule.employee')} />
                                            <HeaderCell label={t('salary.schedule.shift')} />
                                            <HeaderCell label={t('salary.schedule.weekend')} />
                                            <HeaderCell label={t('salary.schedule.holiday')} />
                                            <HeaderCell label="" />
                                        </Box>
                                    </Box>
                                    <Box component="tbody">
                                        {entries.map(entry => (
                                            <Box component="tr" key={entry.id}>
                                                <BodyCell>{entry.date}</BodyCell>
                                                <BodyCell>
                                                    {employeeLabel(entry.employeeId)}
                                                </BodyCell>
                                                <BodyCell>{shiftLabel(entry.shiftId)}</BodyCell>
                                                <BodyCell>
                                                    {entry.isWeekend ? '\u2713' : ''}
                                                </BodyCell>
                                                <BodyCell>
                                                    {entry.isHoliday ? '\u2713' : ''}
                                                </BodyCell>
                                                <BodyCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteEntry(entry.id)}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </BodyCell>
                                            </Box>
                                        ))}
                                        {entries.length === 0 && (
                                            <Box component="tr">
                                                <Box
                                                    component="td"
                                                    colSpan={6}
                                                    sx={{ textAlign: 'center', py: 2 }}
                                                >
                                                    <Typography color="text.secondary">
                                                        {t('salary.schedule.noData')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </>
            )}

            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack({ ...snack, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={snack.severity}
                    onClose={() => setSnack({ ...snack, open: false })}
                >
                    {snack.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={confirmDelete !== null}
                onClose={() => setConfirmDelete(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {confirmDelete === 'regenerate'
                        ? t('salary.schedule.regenerateMonth')
                        : t('salary.schedule.deleteMonth')}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2">
                        {confirmDelete === 'regenerate'
                            ? t('salary.schedule.regenerateConfirm', {
                                  year,
                                  month: pad2(month),
                              })
                            : t('salary.schedule.deleteMonthConfirm', {
                                  year,
                                  month: pad2(month),
                              })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)} disabled={deletingMonth}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteMonthConfirmed}
                        disabled={deletingMonth}
                    >
                        {deletingMonth ? <CircularProgress size={20} /> : t('common.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={proposal !== null} onClose={handleRejectProposal} maxWidth="md" fullWidth>
                <DialogTitle>{t('salary.schedule.proposalTitle')}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('salary.schedule.proposalSubtitle', {
                            count: proposal?.length ?? 0,
                        })}
                    </Typography>
                    <TableContainer sx={{ maxHeight: 420 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('salary.schedule.date')}</TableCell>
                                    <TableCell>{t('salary.schedule.employee')}</TableCell>
                                    <TableCell>{t('salary.schedule.shift')}</TableCell>
                                    <TableCell>{t('salary.schedule.weekend')}</TableCell>
                                    <TableCell>{t('salary.schedule.holiday')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(proposal ?? []).map((entry, index) => (
                                    <TableRow
                                        key={`${entry.date}-${entry.employeeId.toString()}-${entry.shiftId.toString()}-${index.toString()}`}
                                    >
                                        <TableCell>{entry.date}</TableCell>
                                        <TableCell>{employeeLabel(entry.employeeId)}</TableCell>
                                        <TableCell>{shiftLabel(entry.shiftId)}</TableCell>
                                        <TableCell>{entry.isWeekend ? '\u2713' : ''}</TableCell>
                                        <TableCell>{entry.isHoliday ? '\u2713' : ''}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRejectProposal} disabled={committing}>
                        {t('salary.schedule.rejectProposal')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAcceptProposal}
                        disabled={committing}
                    >
                        {committing ? (
                            <CircularProgress size={20} />
                        ) : (
                            t('salary.schedule.acceptProposal')
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

interface DayCellProps {
    iso: string;
    isHoliday: boolean;
    isWeekend: boolean;
    shifts: SalaryShift[];
    dayEntries: SalaryScheduleEntry[];
    employeeLabel: (id: number) => string;
    mondayConflicts: Set<string>;
    onDrop: (event: DragEvent<HTMLDivElement>, date: string, shiftId: number) => void;
    onDragStart: (event: DragEvent<HTMLDivElement>, payload: DragPayload) => void;
    onDragOver: (event: DragEvent<HTMLDivElement>) => void;
    onDeleteEntry: (entryId: number) => void;
}

const DayCell = ({
    iso,
    isHoliday,
    isWeekend,
    shifts,
    dayEntries,
    employeeLabel,
    mondayConflicts,
    onDrop,
    onDragStart,
    onDragOver,
    onDeleteEntry,
}: DayCellProps) => {
    const { t } = useTranslation();
    const dayNumber = Number(iso.slice(-2));

    const bgcolor = (() => {
        if (isHoliday) return 'rgba(244, 67, 54, 0.08)';
        if (isWeekend) return 'rgba(255, 193, 7, 0.08)';
        return 'background.paper';
    })();

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 0.5,
                minHeight: 120,
                bgcolor,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {dayNumber}
                </Typography>
                {isHoliday && (
                    <Typography variant="caption" color="error">
                        {t('salary.schedule.holiday')}
                    </Typography>
                )}
            </Stack>
            <Stack spacing={0.5} sx={{ mt: 0.5, flex: 1 }}>
                {shifts.map(shift => {
                    const slotEntries = dayEntries.filter(entry => entry.shiftId === shift.id);
                    return (
                        <Box
                            key={shift.id}
                            onDragOver={onDragOver}
                            onDrop={event => onDrop(event, iso, shift.id)}
                            sx={{
                                border: '1px dashed',
                                borderColor: 'divider',
                                borderRadius: 0.5,
                                p: 0.5,
                                minHeight: 34,
                                bgcolor: shift.isNight ? 'rgba(63, 81, 181, 0.08)' : 'transparent',
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                {shift.name} {shift.startTime}-{shift.endTime}
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {slotEntries.map(entry => {
                                    const conflict = mondayConflicts.has(
                                        `${entry.employeeId}:${entry.date}`,
                                    );
                                    return (
                                        <Tooltip
                                            key={entry.id}
                                            title={
                                                conflict
                                                    ? t('salary.schedule.mondayConflict')
                                                    : employeeLabel(entry.employeeId)
                                            }
                                        >
                                            <Chip
                                                label={employeeLabel(entry.employeeId)}
                                                size="small"
                                                color={conflict ? 'warning' : 'default'}
                                                icon={conflict ? <WarningAmberIcon /> : undefined}
                                                draggable
                                                onDragStart={event =>
                                                    onDragStart(event, {
                                                        kind: 'entry',
                                                        employeeId: entry.employeeId,
                                                        entryId: entry.id,
                                                    })
                                                }
                                                onDelete={() => onDeleteEntry(entry.id)}
                                                sx={{ cursor: 'grab' }}
                                            />
                                        </Tooltip>
                                    );
                                })}
                            </Stack>
                        </Box>
                    );
                })}
                {shifts.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                        {t('salary.schedule.noShiftsForDay')}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};

const HeaderCell = ({ label }: { label: string }) => (
    <Box
        component="th"
        sx={{
            textAlign: 'left',
            px: 1,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            fontWeight: 600,
        }}
    >
        {label}
    </Box>
);

const BodyCell = ({ children }: { children?: React.ReactNode }) => (
    <Box component="td" sx={{ px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        {children}
    </Box>
);

export default ScheduleTab;
